import React from 'react';
import { Box, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { AdvisoryQuoteLabel, quoteLabelColor, quoteLabelTone } from './AdvisoryQuoteLabel';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';
import type { AdvisorySourceQuote, AdvisorySourceReferenceV01 } from './advisoryProvenance';

export function AdvisorySourceMetadata({
  source,
  checkStatus,
  quoteReferences = [],
  currentQuoteIndex,
}: {
  source: AdvisorySourceReferenceV01;
  checkStatus?: AdvisorySourceQuote['status'];
  quoteReferences?: readonly AdvisoryQuoteReference[];
  currentQuoteIndex?: number;
}) {
  const { t } = useUntypedTranslation();
  const pages = source.pageRange
    ? source.pageRange[0] === source.pageRange[1]
      ? t('advisory:brief.sources.page', { page: String(source.pageRange[0]) })
      : t('advisory:brief.sources.pages', {
          start: String(source.pageRange[0]),
          end: String(source.pageRange[1]),
        })
    : source.locator ?? t('advisory:brief.sources.pageUnknown');
  const location = source.kind === 'document' ? pages : source.locator;
  return (
    <Box
      sx={theme => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        columnGap: 1.5,
        rowGap: 0.5,
        marginTop: 0.75,
        color: theme.colors.inkTertiary,
      })}
    >
      {Array.from(
        new Map(quoteReferences.map(reference => [reference.label, reference])).values()
      ).map(reference => (
        <AdvisoryQuoteLabel
          key={reference.label}
          label={reference.label}
          tone={quoteLabelTone(reference.quote.status)}
          current={reference.quoteIndex === currentQuoteIndex}
        />
      ))}
      <Typography component="span" sx={{ fontSize: 12 }}>
        {t(`advisory:brief.sources.kind.${source.kind}`)}
      </Typography>
      {location ? (
        <Typography component="span" sx={{ fontSize: 12 }}>
          {location}
        </Typography>
      ) : null}
      {checkStatus ? (
        <Typography
          component="span"
          data-source-check={checkStatus}
          sx={theme => ({
            color: quoteLabelColor(theme, quoteLabelTone(checkStatus)),
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
          })}
        >
          {t(`advisory:brief.sources.check.${checkStatus}`)}
        </Typography>
      ) : null}
      {(!checkStatus || source.origin !== 'retrieved') &&
      (source.kind === 'document' || source.origin !== 'unknown') ? (
        <Typography
          component="span"
          data-source-origin={source.origin}
          sx={theme => ({
            color:
              source.origin === 'computed'
                ? theme.colors.lavenderDeep
                : source.origin === 'unknown'
                ? theme.colors.inkTertiary
                : theme.colors.inkSecondary,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
          })}
        >
          {t(`advisory:brief.sources.origin.${source.origin}`)}
        </Typography>
      ) : null}
    </Box>
  );
}
